import React, { useEffect, useState } from "react";
// ui ORM
import CardSurgicalPositionUX from "./CardSurgicalPositionUX";
import PreOPButtonAction from "./PreOPButtonAction";
import { useIntl } from "react-intl";
import { Button, Dropdown, Form, FormField, FormGroup } from "semantic-ui-react";

const CARD_KEY: string = "surgicalPositionData";
const FORM_CODE: string = "CardSurgicalPosition";
const FORM_NAME: string = "Surgical Position & Positional Device (A)";
const FORM_VERSION: string = "1.0";

type CardSurgicalPositionProps = {
  // function
  onEvent: any;
  setProp: any;

  // CommonInterface
  buttonLoadCheck?: any;

  // select OR
  selectedOrOrder?: any;

  // options
  masterOptions?: any;

  // seq
  runSequence?: any;
  PerioperativeNursingSequence?: any;
};

type OptionType = {
  key: string;
  text: string;
  value: string;
};

type OptionsState = {
  planAndImpleHrOptions: OptionType[];
  orMedicationOptions: OptionType[];
  orNDOptions: OptionType[];
  orGoal: OptionType[];
  orEvaluation: OptionType[];
};

const CardSurgicalPosition = (props: CardSurgicalPositionProps) => {
  const [options, setOptions] = useState<OptionsState>({
    planAndImpleHrOptions: [],
    orMedicationOptions: [],
    orNDOptions: [],
    orGoal: [],
    orEvaluation: [],
  });

  const surgicalPositionData = props.PerioperativeNursingSequence?.surgicalPositionData?.data;

  useEffect(() => {
    props.runSequence({
      sequence: "PerioperativeNursing",
    });
  }, []);

  const updateOptions = (
    holdingRoomData: any,
    masterKey: string,
    optionsKey: keyof OptionsState,
    mapHoldingRoomData: (item: any) => OptionType
  ) => {
    const masterData = props.masterOptions?.[masterKey] || [];

    // แก้ไขข้อมูลให้อยู่ในรูปแบบ array
    let valueInOption = Array.isArray(holdingRoomData) ? holdingRoomData : [holdingRoomData];

    // ค้นหา option ที่เพิ่มขึ้นมาเอง
    const optionData = (valueInOption || [])
      .filter(
        (item: any) =>
          (item && typeof item === "string") ||
          (item?.medicine_name && typeof item?.medicine_name === "string")
      )
      .map(mapHoldingRoomData);

    // รวม option จาก masterData และ options (option ที่เพิ่มขึ้นเอง)
    const combinedOption = [...new Set([...masterData, ...(options[optionsKey] || [])])];

    // ตรวจสอบหาเฉพาะ optionData ที่ไม่ซ้ำใน combinedOption
    const newOptions = optionData.filter(
      (item: any) => !combinedOption.some((option) => option.value === item.value)
    );

    setOptions((prevOptions) => ({
      ...prevOptions,
      [optionsKey]: newOptions.length ? [...combinedOption, ...newOptions] : combinedOption,
    }));
  };

  useEffect(() => {
    const nursingDiagnosisData = surgicalPositionData?.nursing_diagnosis_item;

    const allNursingDiagnosis = [
      ...new Set((nursingDiagnosisData || []).flatMap((item: any) => item?.nursing_diagnosis)),
    ];
    updateOptions(allNursingDiagnosis, "orNursingDiagnosis", "orNDOptions", (item) => ({
      key: item,
      text: item,
      value: item,
    }));

    const allGoal = [...new Set((nursingDiagnosisData || []).flatMap((item: any) => item?.goal))];
    updateOptions(allGoal, "orGoal", "orGoal", (item) => ({
      key: item,
      text: item,
      value: item,
    }));

    const allPlanAndImpleHr = [
      ...new Set(
        (nursingDiagnosisData || []).flatMap((item: any) => item?.plan_and_implementation)
      ),
    ];
    updateOptions(allPlanAndImpleHr, "planAndImpleHr", "planAndImpleHrOptions", (item) => ({
      key: item,
      text: item,
      value: item,
    }));

    const allEvaluations = [
      ...new Set((nursingDiagnosisData || []).flatMap((item: any) => item?.evaluation)),
    ];
    updateOptions(allEvaluations, "orEvaluation", "orEvaluation", (item) => ({
      key: item,
      text: item,
      value: item,
    }));
  }, [
    props.masterOptions?.orNursingDiagnosis,
    props.masterOptions?.orGoal,
    props.masterOptions?.planAndImpleHr,
    props.masterOptions?.orEvaluation,
    surgicalPositionData?.nursing_diagnosis_item,
  ]);

  const handleSave = () => {
    props.runSequence({
      sequence: "PerioperativeNursing",
      action: "SAVE",
      cardKey: CARD_KEY,
      formCode: FORM_CODE,
      formName: FORM_NAME,
      formVersion: FORM_VERSION,
    });
  };

  const handleConfirm = () => {
    props.runSequence({
      sequence: "PerioperativeNursing",
      action: "CONFIRM",
      cardKey: CARD_KEY,
      formCode: FORM_CODE,
      formName: FORM_NAME,
      formVersion: FORM_VERSION,
    });
  };

  const handleUnconfirm = () => {
    props.runSequence({
      sequence: "PerioperativeNursing",
      action: "UNCONFIRM",
      cardKey: CARD_KEY,
      formCode: FORM_CODE,
      formName: FORM_NAME,
      formVersion: FORM_VERSION,
    });
  };

  const handleChangeData = (_event: any, data: any) => {
    let value = "";
    if (data.type === "checkbox") {
      value = data.checked;
    } else {
      value = data.value;
    }
    props.runSequence({
      sequence: "PerioperativeNursing",
      action: "SET_DATA",
      cardKey: CARD_KEY,
      key: data.name,
      value: value,
    });
  };

  const handleAddOptions = (e: any, v: any) => {
    let newOption = { text: v.value, value: v.value, key: v.value };

    const optionMap: { [key: string]: keyof OptionsState } = {
      plan_and_implementation: "planAndImpleHrOptions",
      nursing_diagnosis: "orNDOptions",
      goal: "orGoal",
      evaluation: "orEvaluation",
    };

    const key = optionMap[v.name];
    if (key) {
      setOptions({ ...options, [key]: [...options[key], newOption] });
    }
  };

  const handleChangeArrayData = (key: string, index: number, data: any) => {
    let result = [...surgicalPositionData?.[key]];
    result[index][data.name] = data.value;

    props.runSequence({
      sequence: "PerioperativeNursing",
      action: "SET_DATA",
      cardKey: CARD_KEY,
      key: "nursing_diagnosis_item",
      value: result,
    });
  };

  const handleAddArrayData = () => {
    let result = [...surgicalPositionData?.nursing_diagnosis_item];

    result.push({
      nursing_diagnosis: "",
      goal: [],
      plan_and_implementation: [],
      evaluation: [],
    });

    props.setProp(
      "PerioperativeNursingSequence.surgicalPositionData.data.nursing_diagnosis_item",
      result
    );
  };

  const handleRemoveArrayData = (index: number) => {
    let result = [...surgicalPositionData?.nursing_diagnosis_item];
    result.splice(index, 1);

    props.setProp(
      "PerioperativeNursingSequence.surgicalPositionData.data.nursing_diagnosis_item",
      result
    );
  };

  return (
    <>
      <CardSurgicalPositionUX
        masterOptions={props.masterOptions}
        handleChangeData={handleChangeData}
        PerioperativeNursingSequence={props.PerioperativeNursingSequence}
        nurseDiagnosis={surgicalPositionData?.nursing_diagnosis}
        goal={surgicalPositionData?.goal}
        position={surgicalPositionData?.position}
        warmer={surgicalPositionData?.warmer}
        circulatorSupport={surgicalPositionData?.circulator_support}
        device={surgicalPositionData?.device}
        planAndImplementation={surgicalPositionData?.plan_and_implementation}
        status={props.PerioperativeNursingSequence?.surgicalPositionData?.status}
        buttonSave={
          <PreOPButtonAction
            setProp={props.setProp}
            data={props.PerioperativeNursingSequence}
            type="save"
            cardKey={CARD_KEY}
            buttonLoadCheck={props.buttonLoadCheck}
            onClick={handleSave}
          />
        }
        buttonConfirm={
          <PreOPButtonAction
            setProp={props.setProp}
            data={props.PerioperativeNursingSequence}
            type="confirm"
            cardKey={CARD_KEY}
            buttonLoadCheck={props.buttonLoadCheck}
            onClick={handleConfirm}
          />
        }
        buttonUnconfirm={
          <PreOPButtonAction
            setProp={props.setProp}
            data={props.PerioperativeNursingSequence}
            type="unconfirm"
            cardKey={CARD_KEY}
            buttonLoadCheck={props.buttonLoadCheck}
            onClick={handleUnconfirm}
          />
        }
        nursingDiagnosisItem={
          <>
            {(surgicalPositionData?.nursing_diagnosis_item || [])?.map(
              (item: any, index: number) => {
                return (
                  <Form>
                    <FormGroup
                      style={{
                        backgroundColor: "rgb(253, 248, 221)",
                        padding: "30px 10px",
                        marginTop: "2rem",
                      }}
                    >
                      <FormField width={16}>
                        <FormGroup inline={true}>
                          <FormField inline={true} width={7}>
                            <label style={{ width: "100%", maxWidth: "max-content" }}>
                              Nursing Diagnosis
                            </label>
                            <Dropdown
                              allowAdditions={true}
                              clearable={true}
                              fluid={true}
                              name="nursing_diagnosis"
                              onAddItem={handleAddOptions}
                              onChange={(_event: any, data: any) =>
                                handleChangeArrayData("nursing_diagnosis_item", index, data)
                              }
                              options={options.orNDOptions || []}
                              search={true}
                              selection={true}
                              style={{ width: "100%" }}
                              value={item?.nursing_diagnosis}
                            ></Dropdown>
                          </FormField>
                          <FormField inline={true} width={8}>
                            <label>Goal</label>
                            <Dropdown
                              allowAdditions={true}
                              clearable={true}
                              fluid={true}
                              multiple={true}
                              name="goal"
                              onAddItem={handleAddOptions}
                              onChange={(_event: any, data: any) =>
                                handleChangeArrayData("nursing_diagnosis_item", index, data)
                              }
                              options={options.orGoal || []}
                              search={true}
                              selection={true}
                              style={{ width: "100%" }}
                              value={item?.goal || []}
                            ></Dropdown>
                          </FormField>
                          <FormField
                            inline={true}
                            width={1}
                            style={{
                              minWidth: "max-content",
                              padding: 0,
                              marginRight: "-0.5rem",
                            }}
                          >
                            {index === 0 ? (
                              <Button
                                color="green"
                                icon="plus"
                                size="mini"
                                style={{ margin: "0 0 0 10px" }}
                                onClick={handleAddArrayData}
                              ></Button>
                            ) : (
                              <Button
                                color="red"
                                icon="minus"
                                size="mini"
                                style={{ margin: "0 0 0 10px" }}
                                onClick={() => handleRemoveArrayData(index)}
                              ></Button>
                            )}
                          </FormField>
                        </FormGroup>
                        <FormGroup inline={true}>
                          <FormField inline={true} width={7}>
                            <label style={{ width: "100%", maxWidth: "max-content" }}>
                              Plan and implementation
                            </label>
                            <Dropdown
                              allowAdditions={true}
                              clearable={true}
                              fluid={true}
                              multiple={true}
                              name="plan_and_implementation"
                              onAddItem={handleAddOptions}
                              onChange={(_event: any, data: any) =>
                                handleChangeArrayData("nursing_diagnosis_item", index, data)
                              }
                              options={options.planAndImpleHrOptions || []}
                              search={true}
                              selection={true}
                              style={{ width: "100%" }}
                              value={item.plan_and_implementation || []}
                            ></Dropdown>
                          </FormField>
                          <FormField inline={true} width={8}>
                            <label>Evaluation</label>
                            <Dropdown
                              allowAdditions={true}
                              clearable={true}
                              fluid={true}
                              multiple={true}
                              name="evaluation"
                              onAddItem={handleAddOptions}
                              onChange={(_event: any, data: any) =>
                                handleChangeArrayData("nursing_diagnosis_item", index, data)
                              }
                              options={options.orEvaluation}
                              search={true}
                              selection={true}
                              style={{ width: "100%" }}
                              value={item?.evaluation || []}
                            ></Dropdown>
                          </FormField>
                          <FormField inline={true} width={1}></FormField>
                        </FormGroup>
                      </FormField>
                    </FormGroup>
                  </Form>
                );
              }
            )}
          </>
        }
        languageUX={props.languageUX}
      />
    </>
  );
};

export default React.memo(CardSurgicalPosition);
